import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "@phosphor-icons/react";
import PressShareBlock from "@/components/site/PressShareBlock";
import PressVideoFeature from "@/components/site/PressVideoFeature";
import { FEATURED_PRESS, FEATURED_VIDEO, PRESS } from "@/data/press";

// Credibility moment on /legacy — sits after the tribute story, right
// before the signup form. Image-forward, editorial, links out to the
// article and across to /news.
export default function SacrificeFeaturedIn() {
  const item = FEATURED_PRESS;
  if (!item) return null;

  return (
    <section
      data-testid="legacy-featured-in"
      className="px-4 md:px-8 py-16 md:py-24 border-t border-white/10 bg-[#0C0C0C]"
    >
      <div className="max-w-6xl mx-auto">
        <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4 flex items-center gap-3">
          <Newspaper size={14} weight="fill" /> / Featured in
        </p>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl mb-10 md:mb-12">
          The story made <span className="text-[#B32027]">the news.</span>
        </h2>

        {/* News 12 segment — the actual video, playing right here. */}
        {FEATURED_VIDEO && (
          <div className="mb-12 md:mb-14">
            <PressVideoFeature item={FEATURED_VIDEO} />
          </div>
        )}

        <div className="border border-white/10 bg-[#111111]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Image — full, uncropped */}
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="legacy-featured-in-image-link"
              className="group block bg-black overflow-hidden"
            >
              <img
                src={item.image}
                alt={item.imageAlt}
                data-testid="legacy-featured-in-image"
                className="w-full h-full max-h-[520px] object-contain object-center transition-transform duration-700 md:group-hover:scale-[1.03]"
              />
            </a>

            {/* Copy */}
            <div className="p-6 md:p-10 flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5">
                <span
                  data-testid="legacy-featured-in-outlet"
                  className="inline-flex items-center bg-[#B32027] text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.28em]"
                >
                  {item.outlet}
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                  {item.outletNote}
                </span>
              </div>

              <h3
                data-testid="legacy-featured-in-headline"
                className="font-display font-black uppercase tracking-tight leading-[0.95] text-2xl md:text-3xl mb-4"
              >
                {item.headline}
              </h3>

              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-5">
                {item.dek}
              </p>

              <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 mb-7">
                {item.author}
                {item.authorTitle ? ` · ${item.authorTitle}` : ""} ·{" "}
                {item.dateLabel}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="legacy-featured-in-read-story"
                  className="inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors"
                >
                  Read the story <ArrowUpRight size={12} weight="bold" />
                </a>
                <Link
                  to="/news"
                  data-testid="legacy-featured-in-all-coverage"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-6 py-4 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors"
                >
                  {PRESS.length > 1 ? "See all coverage" : "In the news"}
                </Link>
              </div>

              {item.imageCredit && (
                <p className="mt-6 text-[11px] text-white/40 italic">
                  Photo: {item.imageCredit}
                </p>
              )}
            </div>
          </div>
        </div>

        {item.shareCardId && (
          <div className="mt-6">
            <PressShareBlock item={item} compact />
          </div>
        )}
      </div>
    </section>
  );
}
